/**
 * @file Especialização Slider do pipeline Input.
 * @module components/create/slider
 * @see module:components/create/input
 */

import '../../styles/components/input/slider-skin2.scss';

/**
 * Implementação do Slider.
 *
 * @class
 */
class Slider {
  counter = 0;

  /**
   * Normaliza opções do Slider antes da criação do input range.
   *
   * Gera `id`/`name` quando ausentes, corrige min/max, cria ticks padrão,
   * define dimensões padrão de 10rem e instala callback `input` interno para o
   * display quando `display:true`.
   *
   * @param {Object} options Opções recebidas pelo componente.
   * @param {number} [options.min=0] Valor mínimo. Se maior que `max`, os valores são invertidos.
   * @param {number} [options.max=100] Valor máximo.
   * @param {number} [options.step=1] Incremento do range.
   * @param {number} [options.value] Valor inicial; usa `min` quando ausente ou inválido.
   * @param {Array<string|number>} [options.list] Labels dos ticks. Quando ausente, é gerada automaticamente a partir do range.
   * @param {'horizontal'|'vertical'|string} [options.orient='horizontal'] Orientação.
   * @param {boolean} [options.display=false] Exibe valor corrente junto ao slider.
   * @param {1|2|number|string} [options.skin] Apenas `2` ativa a skin alternativa.
   * @returns {Object} O mesmo objeto `options` normalizado.
   * @see module:components/create/input
   */
  slider(options) {
    if(!options.id){
      this.counter++;
      options.id = `slider-${this.counter}`;
    }

    if(!options.name){
      options.name = options.id;
    }

    options.type = 'range';
    if (Number(options.skin) === 2) {
      options.skin = 2;
    } else {
      delete options.skin;
    }

    if (!options.min || typeof options.min !== 'number') {
      options.min = 0;
    }

    if (!options.max || typeof options.max !== 'number') {
      options.max = 100;
    }

    if (options.min > options.max) {
      [options.min, options.max] = [options.max, options.min];
    }

    if (!options.list) {
      let range = options.max - options.min;
      let divisor = 0;

      for (let i = 14; i > 0; i -= 2) {
        if (range % i === 0) {
          divisor = i;
          break;
        }
      }

      if (divisor === 0) {
        divisor = 10;
      }

      const step = range / divisor;
      options.list = [];

      for (let i = 0; i <= divisor; i++) {
        const value = Math.round((i * step) + options.min);
        options.list.push(i % 2 === 0 ? value : '');
      }
    }

    if (!options.step || typeof options.step !== 'number') {
      options.step = 1;
    }

    if (!options.value || typeof options.value !== 'number') {
      options.value = options.min;
    }

    options.style = options.style || {};

    if (options.orient && typeof options.orient === 'string' && options.orient.toLowerCase() === 'vertical') {
      options.orient = 'vertical';
      options.style.height = options.style.height || '10rem';
    } else {
      options.orient = 'horizontal';
      options.style.width = options.style.width || '10rem';
    }

    options.input = (e) => {
      if(options?.display === true){
        const input = e.currentTarget;
        const display = document.getElementById(input.id + '-display');
        display.textContent = input.value;
      }
    }

    return options;
  }

  /**
   * Monta ticks, display e skin adicional depois que o input já está no DOM.
   *
   * @param {HTMLInputElement} elem Input range.
   * @param {Object} options Opções já normalizadas por {@link Slider#slider}.
   * @returns {void}
   * @see Slider#slider
   */
  sliderTicks(elem, options) {
    options.style = options.style || {};

    const parent = (typeof options.parent === 'object') ? options.parent : document.querySelector(options.parent);
    if (!parent) return;

    if (options.skin === 2) {
      this.#skin2(elem);
    }

    if (options.list !== undefined && Array.isArray(options.list) && options.list.length > 1) {
      const ticksWrapper = document.createElement('div');
      const classname = options.orient === 'vertical' ? 'vertical-ticks' : 'horizontal-ticks';
      ticksWrapper.classList.add(classname);
      if (options.skin === 2) {
        ticksWrapper.classList.add('slider-skin-2-ticks');
      }

      options.list.forEach((value) => {
        const tick = document.createElement('span');
        tick.innerText = value;
        ticksWrapper.appendChild(tick);
      });

      if (options.orient === 'horizontal') {
        parent.style.paddingBottom = '24px';
      } else {
        parent.style.paddingRight = '24px';
      }

      parent.appendChild(ticksWrapper);
      this.#adjust(ticksWrapper, options);

      if(options?.display === true){
        this.#display(ticksWrapper, elem, options);
      }
    }
  }

  /**
   * Aplica a skin 2 e mantém a variável CSS de progresso sincronizada.
   *
   * @param {HTMLInputElement} elem Input range.
   * @returns {void}
   */
  #skin2(elem) {
    elem.classList.add('slider-skin-2-input');

    /** Atualiza percentual visual da skin. @returns {void} */
    const updateProgress = () => {
      const min = Number(elem.min);
      const max = Number(elem.max);
      const value = Number(elem.value);
      const range = max - min;
      const progress = range > 0 ? ((value - min) / range) * 100 : 0;
      elem.style.setProperty('--slider-skin-2-progress', `${Math.max(0, Math.min(100, progress))}%`);
    };

    elem.addEventListener('input', updateProgress);
    elem.addEventListener('change', updateProgress);
    updateProgress();
  }

  /**
   * Ajusta geometricamente o wrapper de ticks à dimensão configurada.
   *
   * @param {HTMLElement} ticksWrapper Wrapper.
   * @param {Object} options Opções já normalizadas por {@link Slider#slider}.
   * @returns {void}
   * @see Slider#slider
   */
  #adjust(ticksWrapper, options){
    if (options.orient === 'horizontal') {
      if (options.style.width.endsWith('rem')) {
        const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
        const widthInPx = parseFloat(options.style.width) * rootFontSize;
        options.style.width = `${widthInPx}px`;
      }

      const sliderWidth = parseFloat(options.style.width);
      const positionAdjustment = Math.floor(-0.0375 * sliderWidth) + 19;
      const sizeAdjustment = Math.ceil(1.05625 * sliderWidth) - 31;

      ticksWrapper.style.left = `${positionAdjustment}px`;
      ticksWrapper.style.width = `${sizeAdjustment}px`;
    } else {
      if (options.style.height.endsWith('rem')) {
        const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
        const heightInPx = parseFloat(options.style.height) * rootFontSize;
        options.style.height = `${heightInPx}px`;
      }

      let sliderHeight = parseFloat(options.style.height);

      const positionAdjustment = Math.floor(-0.0375 * sliderHeight) + 33;
      const sizeAdjustment = Math.ceil(1.08 * sliderHeight) - 37;

      ticksWrapper.style.top = `${positionAdjustment}px`;
      ticksWrapper.style.height = `${sizeAdjustment}px`;
    }
  }

  /**
   * Cria o display opcional do valor corrente.
   *
   * @param {HTMLElement} ticksWrapper Wrapper dos ticks.
   * @param {HTMLInputElement} elem Input.
   * @param {Object} options Opções já normalizadas por {@link Slider#slider}.
   * @returns {void}
   * @see Slider#slider
   */
  #display(ticksWrapper, elem, options){
    const span = document.createElement('span');
    span.id = `${elem.id}-display`;
    const classname = options.orient === 'vertical' ? 'vertical-sdisplay' : 'horizontal-sdisplay';
    span.classList.add(classname);
    if (options.skin === 2) {
      span.classList.add('slider-skin-2-display');
    }
    span.textContent = elem.value;
    const parent = ticksWrapper.parentElement;
    parent.appendChild(span);
  }
}

const s = new Slider();

/**
 * Normaliza opções do Slider.
 *
 * @param {Object} options Opções recebidas pelo componente.
 * @returns {Object} O mesmo objeto `options` normalizado.
 * @see Slider#slider
 */
export const slider = (options) => s.slider(options);

/**
 * Monta ticks/skin/display após criação do input.
 *
 * @param {HTMLInputElement} elem Input.
 * @param {Object} options Opções já normalizadas por {@link slider}.
 * @returns {void}
 * @see Slider#sliderTicks
 */
export const sliderTicks = (elem, options) => s.sliderTicks(elem, options);